import axios, { type AxiosInstance } from "axios";

import { attachmentFilenameOf } from "@thalamos/frontend";
import {
  FormDraftSchema,
  GuestAccessLinkOpenedSchema,
  GuestAssessmentSchema,
  GuestDateOfBirthOutcomeSchema,
  GuestSessionSchema,
  GuestSignInStepOutcomeSchema,
  SharedAssessmentSchema,
  SharedWithGuestSchema,
  SubmissionRecordedSchema,
  type AssessmentId,
  type DateOfBirth,
  type FormDraft,
  type GuestAccessLinkOpened,
  type GuestAssessment,
  type GuestDateOfBirthOutcome,
  type GuestSession,
  type GuestSignInStepOutcome,
  type SaveFormDraftRequest,
  type SharedAssessment,
  type SharedWithGuest,
  type SubmissionRecorded,
  type SubmitFormDraftRequest,
} from "@thalamos/haven-common";

import { config as appConfig } from "../../config/index.js";
import type { ErrorBody } from "../axios/api.js";
import type { FormDraftResponse } from "../formDraft/api.js";

/**
 * The client a guest's pages talk to the server through. Apart from the staff
 * client because a guest has no sign-in to be sent to: a 401 here is handed
 * to the page, which asks the guest to open their link again.
 */
export const guestAxios = axios.create({
  baseURL: appConfig.backendUrl,
  // The session lives in a cookie scoped to the parent domain.
  withCredentials: true,
  // 4xx resolves onto `response.data` rather than throwing; 5xx still rejects.
  validateStatus: (status) => status >= 200 && status < 500,
});

/**
 * A guest route's answer: the parsed body where the status is the one that
 * carries it, and the error body otherwise, as the staff form's routes answer.
 */
export type GuestResponse<T> = FormDraftResponse<T>;

const parsedOn =
  <T>(status: number, parse: (data: unknown) => T) =>
  (response: { status: number; data: unknown }): GuestResponse<T> =>
    response.status === status
      ? { status: response.status, data: parse(response.data) }
      : { status: response.status, error: response.data as ErrorBody };

const nothing = (): undefined => undefined;

const ROOT = "/api/guest";
const ASSESSMENT = `${ROOT}/assessment`;
const SHARED = `${ROOT}/shared`;

export const config = {
  guest: {
    openLink: (token: string) => ({
      url: `${ROOT}/link/open`,
      method: "post",
      data: { token },
    }),
    sendCode: () => ({ url: `${ROOT}/code/send`, method: "post" }),
    enterCode: (code: string) => ({
      url: `${ROOT}/code/check`,
      method: "post",
      data: { code },
    }),
    enterDateOfBirth: (dateOfBirth: DateOfBirth) => ({
      url: `${ROOT}/date-of-birth/check`,
      method: "post",
      data: { dateOfBirth },
    }),
    acceptAgreement: (wordingDigest: string) => ({
      url: `${ROOT}/agreement/accept`,
      method: "post",
      data: { wordingDigest },
    }),
    decline: () => ({ url: `${ROOT}/link/decline`, method: "post" }),
    session: () => ({ url: `${ROOT}/session`, method: "get" }),
    assessment: () => ({ url: ASSESSMENT, method: "get" }),
    instrument: () => ({ url: `${ASSESSMENT}/instrument`, method: "get" }),
    startDraft: () => ({ url: `${ASSESSMENT}/draft`, method: "post" }),
    resumeDraft: () => ({ url: `${ASSESSMENT}/draft`, method: "get" }),
    saveDraft: (body: SaveFormDraftRequest) => ({
      url: `${ASSESSMENT}/draft`,
      method: "put",
      data: body,
    }),
    send: (body: SubmitFormDraftRequest) => ({
      url: `${ASSESSMENT}/submissions`,
      method: "post",
      data: body,
    }),
    sentPdf: () => ({
      url: `${ASSESSMENT}/sent/pdf`,
      method: "get",
      responseType: "blob" as const,
    }),
    shared: () => ({ url: SHARED, method: "get" }),
    sharedAssessment: (assessmentId: AssessmentId) => ({
      url: `${SHARED}/${assessmentId}`,
      method: "get",
    }),
    sharedInstrument: (assessmentId: AssessmentId) => ({
      url: `${SHARED}/${assessmentId}/instrument`,
      method: "get",
    }),
    sharedPdf: (assessmentId: AssessmentId) => ({
      url: `${SHARED}/${assessmentId}/pdf`,
      method: "get",
      responseType: "blob" as const,
    }),
  },
};

/** A PDF as a route answered it: the file and the name the server gave it, or undefined for a 4xx. */
const pdfOf = (response: {
  status: number;
  data: unknown;
  headers: Record<string, unknown>;
}): { blob: Blob; filename: string } | undefined => {
  const disposition = response.headers["content-disposition"];

  return response.status === 200 && response.data instanceof Blob
    ? {
        blob: response.data,
        filename:
          attachmentFilenameOf(
            typeof disposition === "string" ? disposition : undefined,
          ) ?? "assessment.pdf",
      }
    : undefined;
};

/** The guest session's routes: signing in through a link, and the assessments it reaches. */
export const api = (client: AxiosInstance) => {
  const request = async <T>(
    route: { url: string; method: string; data?: unknown },
    status: number,
    parse: (data: unknown) => T,
  ): Promise<GuestResponse<T>> =>
    parsedOn(status, parse)(await client.request<unknown>(route));
  const routes = config.guest;

  return {
    guest: {
      /** Where the link stands; 404 for a token no link holds. */
      openLink: (
        token: string,
      ): Promise<GuestResponse<GuestAccessLinkOpened>> =>
        request(routes.openLink(token), 200, (data) =>
          GuestAccessLinkOpenedSchema.parse(data),
        ),
      /** 204 once emailed; 409 within `guestAccessCodeResendAfterSeconds` of the last code, or out of turn. */
      sendCode: (): Promise<GuestResponse<undefined>> =>
        request(routes.sendCode(), 204, nothing),
      enterCode: (
        code: string,
      ): Promise<GuestResponse<GuestSignInStepOutcome>> =>
        request(routes.enterCode(code), 200, (data) =>
          GuestSignInStepOutcomeSchema.parse(data),
        ),
      enterDateOfBirth: (
        dateOfBirth: DateOfBirth,
      ): Promise<GuestResponse<GuestDateOfBirthOutcome>> =>
        request(routes.enterDateOfBirth(dateOfBirth), 200, (data) =>
          GuestDateOfBirthOutcomeSchema.parse(data),
        ),
      /** 204 once accepted, which signs the guest in. */
      acceptAgreement: (
        wordingDigest: string,
      ): Promise<GuestResponse<undefined>> =>
        request(routes.acceptAgreement(wordingDigest), 204, nothing),
      decline: (): Promise<GuestResponse<undefined>> =>
        request(routes.decline(), 204, nothing),
      session: (): Promise<GuestResponse<GuestSession>> =>
        request(routes.session(), 200, (data) =>
          GuestSessionSchema.parse(data),
        ),
      assessment: (): Promise<GuestResponse<GuestAssessment>> =>
        request(routes.assessment(), 200, (data) =>
          GuestAssessmentSchema.parse(data),
        ),
      /** The pinned version's schema, unparsed, for `parsePublishedInstrumentSchema` to judge. */
      instrument: async (): Promise<{ status: number; data: unknown }> => {
        const { status, data } = await client.request<unknown>(
          routes.instrument(),
        );
        return { status, data };
      },
      startDraft: (): Promise<FormDraftResponse<FormDraft>> =>
        request(routes.startDraft(), 201, (data) =>
          FormDraftSchema.parse(data),
        ),
      resumeDraft: (): Promise<FormDraftResponse<FormDraft>> =>
        request(routes.resumeDraft(), 200, (data) =>
          FormDraftSchema.parse(data),
        ),
      saveDraft: (
        body: SaveFormDraftRequest,
      ): Promise<FormDraftResponse<FormDraft>> =>
        request(routes.saveDraft(body), 200, (data) =>
          FormDraftSchema.parse(data),
        ),
      /** 201 once sent, which ends the guest's access at once. */
      send: (
        body: SubmitFormDraftRequest,
      ): Promise<GuestResponse<SubmissionRecorded>> =>
        request(routes.send(body), 201, (data) =>
          SubmissionRecordedSchema.parse(data),
        ),
      /**
       * A copy of the assessment just sent, as a PDF to save, named as the
       * server names it; undefined for a 4xx, such as a session that no
       * longer admits it, rejected for a 5xx or a failed request.
       */
      sentPdf: async (): Promise<
        { blob: Blob; filename: string } | undefined
      > => pdfOf(await client.request<unknown>(routes.sentPdf())),
      /** What the link shares, each assessment the guest may still read. */
      shared: (): Promise<GuestResponse<SharedWithGuest>> =>
        request(routes.shared(), 200, (data) =>
          SharedWithGuestSchema.parse(data),
        ),
      /** One shared assessment, with what it says; opening it is recorded. */
      sharedAssessment: (
        assessmentId: AssessmentId,
      ): Promise<GuestResponse<SharedAssessment>> =>
        request(routes.sharedAssessment(assessmentId), 200, (data) =>
          SharedAssessmentSchema.parse(data),
        ),
      /** A shared assessment's pinned version, unparsed, as `instrument` gives the asked one's. */
      sharedInstrument: async (
        assessmentId: AssessmentId,
      ): Promise<{ status: number; data: unknown }> => {
        const { status, data } = await client.request<unknown>(
          routes.sharedInstrument(assessmentId),
        );
        return { status, data };
      },
      /** A shared assessment as a PDF to save; undefined for a 4xx, rejected for a 5xx. */
      sharedPdf: async (
        assessmentId: AssessmentId,
      ): Promise<{ blob: Blob; filename: string } | undefined> =>
        pdfOf(await client.request<unknown>(routes.sharedPdf(assessmentId))),
    },
  };
};

const guestApi = api(guestAxios);

export default guestApi;
